import { OTP_DESTINATION_BLOCKED_REASON } from '~/constants/phoneVerification';

// Maps /api/user/phone-change/{start,confirm} errors to profile.verifyPhonePopup.errors.* i18n keys.

const HTTP_TOO_MANY_REQUESTS = 429;

const ERROR_KEYS = {
  tooManyRequests: 'profile.verifyPhonePopup.errors.tooManyRequests',
  destinationUnavailable: 'profile.verifyPhonePopup.errors.destinationUnavailable',
  destinationBlocked: 'profile.verifyPhonePopup.errors.destinationBlocked',
  invalidPhone: 'profile.verifyPhonePopup.errors.invalidPhone',
  phoneUnchanged: 'profile.verifyPhonePopup.errors.phoneUnchanged',
  phoneInUse: 'profile.verifyPhonePopup.errors.phoneInUse',
  invalidCodeFormat: 'profile.verifyPhonePopup.errors.invalidCodeFormat',
  invalidCode: 'profile.verifyPhonePopup.errors.invalidCode',
  sendFailed: 'profile.verifyPhonePopup.errors.sendFailed',
  generic: 'profile.verifyPhonePopup.errors.generic',
} as const;

export type PhoneChangeErrorKey = (typeof ERROR_KEYS)[keyof typeof ERROR_KEYS];

export const PHONE_CHANGE_GENERIC_ERROR: PhoneChangeErrorKey = ERROR_KEYS.generic;

// Server `data.reason` (PhoneChangeReason / rate limiter) → message.
const BY_REASON: Record<string, keyof typeof ERROR_KEYS> = {
  rate_limited: 'tooManyRequests',
  invalid_phone: 'invalidPhone',
  phone_unchanged: 'phoneUnchanged',
  phone_in_use: 'phoneInUse',
  invalid_code_format: 'invalidCodeFormat',
  invalid_code: 'invalidCode',
  otp_send_failed: 'sendFailed',
  otp_destination_blocked: 'destinationBlocked',
};

// Server `data.code` (errors.* catalogue codes) → message, for errors without a reason.
const BY_CODE: Record<string, keyof typeof ERROR_KEYS> = {
  'otp.tooManyRequests': 'tooManyRequests',
  'otp.tooManyAttempts': 'tooManyRequests',
  'otp.destinationUnavailable': 'destinationUnavailable',
  'user.phoneInUse': 'phoneInUse',
  'otp.invalidCodeFormat': 'invalidCodeFormat',
  'otp.invalidOrExpiredCode': 'invalidCode',
};

type Payload = { reason?: unknown; code?: unknown };

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : null;

// Same three shapes as utils/i18n/errorCode.ts: FetchError, NuxtError, raw response.
const payloadOf = (error: unknown): Payload => {
  const err = asRecord(error);
  const data = asRecord(err?.data);
  const responseBody = asRecord(asRecord(err?.response)?._data);
  const candidates = [asRecord(data?.data), data, asRecord(responseBody?.data)];
  return candidates.find((c) => typeof c?.reason === 'string' || typeof c?.code === 'string') ?? {};
};

const statusOf = (error: unknown): number | null => {
  const err = asRecord(error);
  const status = err?.statusCode ?? err?.status ?? asRecord(err?.response)?.status;
  return typeof status === 'number' ? status : null;
};

export const phoneChangeErrorKey = (error: unknown): PhoneChangeErrorKey => {
  const { reason, code } = payloadOf(error);
  const byReason = typeof reason === 'string' ? BY_REASON[reason] : undefined;
  const byCode = typeof code === 'string' ? BY_CODE[code] : undefined;
  const fallback = statusOf(error) === HTTP_TOO_MANY_REQUESTS ? 'tooManyRequests' : 'generic';
  return ERROR_KEYS[byReason ?? byCode ?? fallback];
};

// The server refused to text the number on file and allows the email code instead.
export const isEmailFallbackOffered = (error: unknown): boolean => {
  const payload = payloadOf(error) as Payload & { emailFallback?: unknown };
  return payload.reason === OTP_DESTINATION_BLOCKED_REASON && payload.emailFallback === true;
};

// Code-entry errors stay inline in the popup; everything else is a banner.
export const isPhoneChangeCodeError = (key: PhoneChangeErrorKey): boolean =>
  key === ERROR_KEYS.invalidCode || key === ERROR_KEYS.invalidCodeFormat;
